import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Check } from "lucide-react";
import { products, productList } from "@/data/products";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

const ProductPage = () => {
  const { slug } = useParams();
  const product = products[slug];

  if (!product) {
    return <Navigate to="/" replace />;
  }

  const t = product.theme;
  const others = productList.filter((p) => p.slug !== slug);
  const isComingSoon = !!product.comingSoon;
  const isExternal = product.cta.href.startsWith("http");

  return (
    <div data-testid={`product-page-${slug}`}>
      {/* ===== BANNER ===== */}
      <section className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20">
        <div
          className={`absolute inset-0 bg-gradient-to-b ${t.softBg} to-transparent`}
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Link
            to="/"
            data-testid="back-to-home-link"
            className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to CognitLoop
          </Link>

          <div className="mt-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <motion.span
                variants={fadeUp}
                initial="hidden"
                animate="show"
                className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider border ${t.border} ${t.badgeBg} ${t.text}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${t.dot}`} />
                {product.category}
              </motion.span>

              <motion.h1
                variants={fadeUp}
                custom={1}
                initial="hidden"
                animate="show"
                className="mt-5 text-4xl sm:text-5xl font-bold tracking-tight leading-tight text-slate-900"
              >
                {product.name}
                <span className={`block mt-2 text-2xl sm:text-3xl ${t.text}`}>
                  {product.tagline}
                </span>
              </motion.h1>

              <motion.p
                variants={fadeUp}
                custom={2}
                initial="hidden"
                animate="show"
                className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl"
              >
                {product.shortDescription}
              </motion.p>

              <motion.div
                variants={fadeUp}
                custom={3}
                initial="hidden"
                animate="show"
                className="mt-8"
              >
                {isComingSoon ? (
                  <a
                    href="/#newsletter"
                    data-testid="product-cta-button"
                    className={`inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold text-white ${t.cta} transition-all hover:scale-[1.03] shadow-lg`}
                  >
                    {product.comingSoonLabel}
                  </a>
                ) : (
                  <a
                    href={product.cta.href}
                    target={isExternal ? "_blank" : undefined}
                    rel={isExternal ? "noopener noreferrer" : undefined}
                    data-testid="product-cta-button"
                    className={`inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold text-white ${t.cta} transition-all hover:scale-[1.03] shadow-lg`}
                  >
                    {product.cta.label}
                    <ArrowUpRight className="h-5 w-5" />
                  </a>
                )}
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className={`relative rounded-3xl overflow-hidden border ${t.border} shadow-xl bg-white`}
            >
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-[320px] sm:h-[400px] object-cover object-center"
                loading="eager"
              />
              <div
                className={`absolute inset-0 bg-gradient-to-t ${t.gradient} opacity-[0.08]`}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== MISSION + AUDIENCE ===== */}
      <section className="py-16 sm:py-24 border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-3 gap-12">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className="lg:col-span-2"
          >
            <p className={`text-sm font-semibold uppercase tracking-widest ${t.text}`}>
              The Mission
            </p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 leading-tight">
              {product.bannerTitle.split("—")[0].trim()}
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              {product.mission}
            </p>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={1}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className={`rounded-2xl border ${t.border} ${t.softBg} p-7 h-fit`}
          >
            <p className="text-sm font-semibold uppercase tracking-widest text-slate-500">
              Target Audience
            </p>
            <p className="mt-3 text-xl font-semibold text-slate-900 leading-snug">
              {product.audience}
            </p>
          </motion.div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            <p className={`text-sm font-semibold uppercase tracking-widest ${t.text}`}>
              Capabilities
            </p>
            <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              What makes it work
            </h2>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {product.features.map((f, i) => (
              <motion.div
                key={f.title}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                data-testid={`feature-card-${i}`}
                className={`bg-white border ${t.border} rounded-3xl p-8 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300`}
              >
                <span
                  className={`flex h-12 w-12 items-center justify-center rounded-2xl ${t.iconBg}`}
                >
                  <f.icon className="h-6 w-6" />
                </span>
                <h3 className="mt-6 text-xl font-semibold text-slate-900">
                  {f.title}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">
                  {f.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* CTA banner */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
            className={`mt-16 rounded-[2rem] border ${t.border} ${t.softBg} p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6`}
          >
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
                {isComingSoon
                  ? `${product.name} enters closed beta in November 2026`
                  : `Ready to try ${product.name}?`}
              </h3>
              <p className="mt-2 text-slate-600 flex items-center gap-2">
                <Check className={`h-4 w-4 ${t.text}`} />
                {isComingSoon
                  ? "Get a seat on the CognitLoop beta list."
                  : product.audience}
              </p>
            </div>
            {isComingSoon ? (
              <a
                href="/#newsletter"
                data-testid="product-cta-button-secondary"
                className={`shrink-0 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold text-white ${t.cta} transition-all hover:scale-[1.03] shadow-lg`}
              >
                {product.comingSoonLabel}
              </a>
            ) : (
              <a
                href={product.cta.href}
                target={isExternal ? "_blank" : undefined}
                rel={isExternal ? "noopener noreferrer" : undefined}
                data-testid="product-cta-button-secondary"
                className={`shrink-0 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full text-base font-semibold text-white ${t.cta} transition-all hover:scale-[1.03] shadow-lg`}
              >
                {product.cta.label}
                <ArrowUpRight className="h-5 w-5" />
              </a>
            )}
          </motion.div>
        </div>
      </section>

      {/* ===== OTHER APPS ===== */}
      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-sm font-semibold uppercase tracking-widest text-slate-400">
            Also in closed beta November 2026
          </p>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {others.map((p) => (
              <Link
                key={p.slug}
                to={`/${p.slug}`}
                data-testid={`related-product-${p.slug}`}
                className={`group bg-white border ${p.theme.border} rounded-2xl p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300`}
              >
                <span
                  className={`inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider ${p.theme.text}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${p.theme.dot}`} />
                  {p.category}
                </span>
                <h4 className="mt-3 text-lg font-bold text-slate-900 flex items-center gap-1.5">
                  {p.name}
                  <ArrowUpRight className="h-4 w-4 text-slate-400 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </h4>
                <p className="mt-1 text-sm text-slate-500">{p.tagline}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductPage;
